import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import {
  PermissionStatus,
  NotificationOptions,
  ReceivedNotification,
  Subscription,
  PendingNotification,
  DeliveredNotification,
  NotificationChannel,
  PlatformAdapter,
  NotificationLog
} from './types';
import { CapacitorNotificationAdapter } from './adapters/CapacitorAdapter';
import { WebNotificationAdapter } from './adapters/WebAdapter';
import { SimpleAdapter } from './adapters/SimpleAdapter';
import { ElectronAdapter } from './adapters/ElectronAdapter';
import { FCMAdapter } from './adapters/FCMAdapter';
import { getPlatformInfo, type PlatformInfo } from '../../utils/platformUtils';

/**
 * 通知服务类
 * 提供跨平台的通知功能支持
 */
export class NotificationService {
  private static instance: NotificationService;

  // 平台信息（使用统一的平台工具）
  private platformInfo: PlatformInfo;
  // 当前平台适配器（用于本地通知）
  private platformAdapter: PlatformAdapter | null = null;
  // 推送通知适配器（用于远程推送）
  private pushAdapter: PlatformAdapter | null = null;
  // 默认通知频道 (Android)
  private defaultChannelId = 'default_channel';
  // 订阅回调列表
  private subscriptions: { [key: string]: Function[] } = {
    received: [],
    action: []
  };
  // 通知日志
  private notificationLogs: NotificationLog[] = [];

  constructor() {
    // 使用统一的平台检测工具
    this.platformInfo = getPlatformInfo();

    this.initPlatformAdapter();
    this.initPushAdapter();

    console.log('[NotificationService] Initialized with platform detection:', this.platformInfo);

    // 监听Service Worker消息
    if (this.platformInfo.isWeb && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
          console.log('[NotificationService] Notification clicked message from SW:', event.data);
          this.triggerActionSubscriptions(event.data.notification);
        } else if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
          console.log('[NotificationService] Push notification message from SW:', event.data);
          this.triggerReceivedSubscriptions(event.data.notification);
        }
      });
    }
  }

  /**
   * 获取服务实例（单例模式）
   */
  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * 打开系统通知设置（特定于支持的平台）
   */
  public async openSystemSettings(): Promise<void> {
    // 检查 platformAdapter 是否存在，并且是否有 openSystemSettings 方法
    if (this.platformAdapter && typeof this.platformAdapter.openSystemSettings === 'function') {
      try {
        console.log('[NotificationService] Attempting to open system settings via adapter.');
        await this.platformAdapter.openSystemSettings();
      } catch (error) {
        console.error('[NotificationService] Error opening system settings via adapter:', error);
      }
    } else {
      console.warn('[NotificationService] Opening system settings not supported by the current platform adapter.');
       // toast({ // 添加 Toast 提示用户 - 无法在此处直接调用 useToast
       //    title: '无法自动打开设置',
       //    description: '请手动打开系统设置来调整通知权限。',
       //    status: 'warning',
       //    duration: 5000,
       //    isClosable: true,
       //  });
    }
  }

  /**
   * 获取当前平台信息
   */
  public getPlatformInfo(): PlatformInfo {
    return this.platformInfo;
  }

  /**
   * 初始化平台适配器（用于本地通知）
   */
  private initPlatformAdapter() {
    // **优先处理 Electron**
    if (this.platformInfo.isElectron) {
      this.platformAdapter = new ElectronAdapter();
      console.log('[NotificationService] Using Electron adapter for local notifications.');
      // **注意：在 Electron 中不调用 initNativeListeners**
    } else if (this.platformInfo.isNative) {
      // 使用 Capacitor 适配器处理本地通知
      this.platformAdapter = new CapacitorNotificationAdapter();
      console.log('[NotificationService] Using Capacitor adapter for local notifications.');
      this.initNativeListeners(); // 只在真正的原生移动平台调用
    } else if (this.platformInfo.isWeb) {
       console.log('[NotificationService] Using Web adapter for local notifications.');
       this.platformAdapter = new WebNotificationAdapter();
    } else {
      console.log('[NotificationService] Using Simple adapter (fallback) for local notifications.');
      this.platformAdapter = new SimpleAdapter();
    }
  }

  /**
   * 初始化推送适配器（用于远程推送）
   */
  private initPushAdapter() {
    // FCM 适配器支持所有平台的推送通知
    this.pushAdapter = new FCMAdapter();
    console.log('[NotificationService] Using FCM adapter for push notifications.');
  }

  /**
   * 初始化原生监听器
   */
  private initNativeListeners() {
    if (!this.platformInfo.isNative) { // 这个判断现在更可靠了
         console.warn('[NotificationService] Skipping native listeners because not on native platform.');
         return;
    }
    console.log('[NotificationService] Initializing native listeners for Capacitor.');
    // 注册本地通知接收监听
    LocalNotifications.addListener('localNotificationReceived', (notification) => {
      console.log('[NotificationService] Local notification received:', notification);
      this.triggerReceivedSubscriptions({
        id: notification.id.toString(),
        title: notification.title,
        body: notification.body,
        data: notification.extra,
        source: 'local'
      });
    });

    // 注册本地通知操作监听
    LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
      console.log('[NotificationService] Local notification action performed:', event);
      this.triggerActionSubscriptions({
        id: event.notification.id.toString(),
        title: event.notification.title,
        body: event.notification.body,
        data: event.notification.extra,
        actionId: event.actionId,
        source: 'local'
      });
    });

    // 注册推送通知接收监听
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('---------------- PUSH NOTIFICATION RECEIVED ----------------');
      console.log('[NotificationService] Raw Push notification received:', JSON.stringify(notification, null, 2));
      this.triggerReceivedSubscriptions({
        id: notification.id,
        title: notification.title,
        body: notification.body,
        data: notification.data,
        source: 'remote'
      });
      console.log('----------------------------------------------------------');
    });

    // 注册推送通知操作监听
    PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
      console.log('[NotificationService] Push notification action performed:', event);
      this.triggerActionSubscriptions({
        id: event.notification.id,
        title: event.notification.title,
        body: event.notification.body,
        data: event.notification.data,
        actionId: event.actionId,
        source: 'remote'
      });
    });
  }

  /**
   * 请求通知权限
   */
  public async requestPermission(): Promise<PermissionStatus> {
    try {
      if (this.platformAdapter) {
        return await this.platformAdapter.requestPermission();
      }
      return 'denied';
    } catch (error) {
      console.error('[NotificationService] Error requesting permission:', error);
      return 'denied';
    }
  }

  /**
   * 检查通知权限状态
   */
  public async checkPermission(): Promise<PermissionStatus> {
    try {
      if (this.platformAdapter) {
        return await this.platformAdapter.checkPermission();
      }
      return 'denied';
    } catch (error) {
      console.error('[NotificationService] Error checking permission:', error);
      return 'denied';
    }
  }

  /**
   * 发送本地通知
   */
  public async scheduleLocalNotification(options: NotificationOptions): Promise<string> {
    try {
      if (!this.platformAdapter) {
        throw new Error('Platform adapter not initialized');
      }

      // 如果没有ID，生成一个随机ID
      if (!options.id) {
        options.id = Math.floor(Math.random() * 1000000).toString();
      }

      // 记录通知日志
      this.logNotification('schedule', options, true);

      return await this.platformAdapter.scheduleLocalNotification(options);
    } catch (error) {
      // 记录错误日志
      this.logNotification('schedule', options, false, error);
      console.error('[NotificationService] Error scheduling notification:', error);
      throw error;
    }
  }

  /**
   * 取消本地通知
   */
  public async cancelLocalNotification(id: string): Promise<void> {
    try {
      if (this.platformAdapter) {
        // 记录通知日志
        this.logNotification('cancel', { id }, true);
        await this.platformAdapter.cancelLocalNotification(id);
      }
    } catch (error) {
      // 记录错误日志
      this.logNotification('cancel', { id }, false, error);
      console.error('[NotificationService] Error cancelling notification:', error);
      throw error;
    }
  }

  /**
   * 取消所有本地通知
   */
  public async cancelAllLocalNotifications(): Promise<void> {
    try {
      if (this.platformAdapter) {
        // 记录通知日志
        this.logNotification('cancelAll', {}, true);
        await this.platformAdapter.cancelAllLocalNotifications();
      }
    } catch (error) {
      // 记录错误日志
      this.logNotification('cancelAll', {}, false, error);
      console.error('[NotificationService] Error cancelling all notifications:', error);
      throw error;
    }
  }

  /**
   * 注册推送通知
   */
  public async registerForPushNotifications(): Promise<string> {
    try {
      // 优先使用专门的推送适配器
      if (this.pushAdapter && this.pushAdapter.registerForPushNotifications) {
        console.log('[NotificationService] Using dedicated push adapter for registration.');
        return await this.pushAdapter.registerForPushNotifications();
      }

      // 降级到平台适配器
      if (this.platformAdapter && this.platformAdapter.registerForPushNotifications) {
        console.log('[NotificationService] Falling back to platform adapter for push registration.');
        return await this.platformAdapter.registerForPushNotifications();
      }

      throw new Error('Push notifications not supported on this platform');
    } catch (error) {
      console.error('[NotificationService] Error registering for push notifications:', error);
      throw error;
    }
  }

  /**
   * 注销推送通知
   */
  public async unregisterForPushNotifications(): Promise<void> {
    try {
      if (this.platformAdapter && this.platformAdapter.unregisterForPushNotifications) {
        await this.platformAdapter.unregisterForPushNotifications();
      }
    } catch (error) {
      console.error('[NotificationService] Error unregistering push notifications:', error);
      throw error;
    }
  }

  /**
   * 获取待处理通知
   */
  public async getPendingNotifications(): Promise<PendingNotification[]> {
    try {
      if (this.platformAdapter && this.platformAdapter.getPendingNotifications) {
        return await this.platformAdapter.getPendingNotifications();
      }
      return [];
    } catch (error) {
      console.error('[NotificationService] Error getting pending notifications:', error);
      return [];
    }
  }

  /**
   * 获取已递送通知
   */
  public async getDeliveredNotifications(): Promise<DeliveredNotification[]> {
    try {
      if (this.platformAdapter && this.platformAdapter.getDeliveredNotifications) {
        return await this.platformAdapter.getDeliveredNotifications();
      }
      return [];
    } catch (error) {
      console.error('[NotificationService] Error getting delivered notifications:', error);
      return [];
    }
  }

  /**
   * 移除已递送通知
   */
  public async removeDeliveredNotifications(ids: string[]): Promise<void> {
    try {
      if (this.platformAdapter && this.platformAdapter.removeDeliveredNotifications) {
        await this.platformAdapter.removeDeliveredNotifications(ids);
      }
    } catch (error) {
      console.error('[NotificationService] Error removing delivered notifications:', error);
      throw error;
    }
  }

  /**
   * 创建通知频道（Android特有）
   */
  public async createChannel(channel: NotificationChannel): Promise<void> {
    try {
      if (this.platformAdapter && this.platformAdapter.createChannel) {
        // 记录通知日志
        this.logNotification('createChannel', { id: channel.id }, true);
        await this.platformAdapter.createChannel(channel);
      }
    } catch (error) {
      // 记录错误日志
      this.logNotification('createChannel', { id: channel.id }, false, error);
      console.error('[NotificationService] Error creating notification channel:', error);
      throw error;
    }
  }

  /**
   * 添加通知接收监听器
   */
  public addNotificationReceivedListener(
    callback: (notification: ReceivedNotification) => void
  ): Subscription {
    const idx = this.subscriptions.received.length;
    this.subscriptions.received.push(callback);

    return {
      remove: () => {
        this.subscriptions.received.splice(idx, 1);
      }
    };
  }

  /**
   * 添加通知操作监听器
   */
  public addNotificationActionListener(
    callback: (notification: ReceivedNotification) => void
  ): Subscription {
    const idx = this.subscriptions.action.length;
    this.subscriptions.action.push(callback);

    return {
      remove: () => {
        this.subscriptions.action.splice(idx, 1);
      }
    };
  }

  /**
   * 触发接收订阅回调
   */
  public triggerReceivedSubscriptions(notification: ReceivedNotification) {
    this.subscriptions.received.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('[NotificationService] Error in received listener:', error);
      }
    });
  }

  /**
   * 触发操作订阅回调
   */
  public triggerActionSubscriptions(notification: ReceivedNotification) {
    this.subscriptions.action.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('[NotificationService] Error in action listener:', error);
      }
    });
  }

  /**
   * 记录通知日志
   */
  private logNotification(
    action: string,
    notification: Partial<NotificationOptions>,
    success: boolean,
    error?: any
  ) {
    // 记录通知操作
    this.notificationLogs.push({
      timestamp: new Date(),
      action,
      notification,
      success,
      error: error ? (error.message || String(error)) : undefined
    });

    // 限制日志长度
    if (this.notificationLogs.length > 100) {
      this.notificationLogs = this.notificationLogs.slice(-100);
    }
  }

  /**
   * 获取通知日志
   */
  public getNotificationLogs() {
    return [...this.notificationLogs];
  }

  /**
   * 清除通知日志
   */
  public clearNotificationLogs() {
    this.notificationLogs = [];
  }

  /**
   * 立即发送通知（便捷方法）
   */
  public async sendNotification(title: string, body: string, options: Partial<NotificationOptions> = {}): Promise<string> {
    return this.scheduleLocalNotification({
      id: options.id || Math.floor(Math.random() * 1000000).toString(),
      title,
      body,
      ...options
    });
  }

  /**
   * 尝试将设备令牌发送到服务器
   * 应该在用户认证成功后调用
   */
  public async registerDeviceWithServer(): Promise<void> {
    console.log('[NotificationService] Attempting to register device with server...')

    // 优先使用专门的推送适配器（FCMAdapter）
    if (this.pushAdapter && typeof (this.pushAdapter as any).attemptSendTokenToServer === 'function') {
      try {
        await (this.pushAdapter as any).attemptSendTokenToServer();
        console.log('[NotificationService] attemptSendTokenToServer called on push adapter.')
      } catch (error) {
        console.error('[NotificationService] Error calling attemptSendTokenToServer on push adapter:', error);
      }
    }
    // 降级到平台适配器
    else if (this.platformAdapter && typeof (this.platformAdapter as any).attemptSendTokenToServer === 'function') {
      try {
        await (this.platformAdapter as any).attemptSendTokenToServer();
        console.log('[NotificationService] attemptSendTokenToServer called on platform adapter.')
      } catch (error) {
        console.error('[NotificationService] Error calling attemptSendTokenToServer on platform adapter:', error);
      }
    } else {
      console.warn('[NotificationService] registerDeviceWithServer called, but no adapter with attemptSendTokenToServer method is available.')
    }
  }

  /**
   * 自动注册设备（用于用户登录后的自动注册）
   * 这个方法会检查权限、注册设备并发送令牌到服务器
   */
  public async autoRegisterDevice(): Promise<void> {
    console.log('[NotificationService] Attempting to auto register device...')

    // 优先使用专门的推送适配器（FCMAdapter）
    if (this.pushAdapter && typeof (this.pushAdapter as any).autoRegisterDevice === 'function') {
      try {
        await (this.pushAdapter as any).autoRegisterDevice();
        console.log('[NotificationService] autoRegisterDevice called on push adapter.')
      } catch (error) {
        console.error('[NotificationService] Error calling autoRegisterDevice on push adapter:', error);
      }
    } else {
      console.warn('[NotificationService] autoRegisterDevice called, but no adapter with autoRegisterDevice method is available.')
    }
  }
}